import type {
  GoogleDocsExportResult,
  GoogleDriveImportResult,
  GoogleNativeImportResult,
  RefreshCourseLinkContentResult,
  ResourceParentLocator,
} from '@/shared/types/resource';
import supabase from '@/shared/libs/supabase';
import { isFeatureEnabled } from '@/shared/libs/featureFlags';

export const GOOGLE_WORKSPACE_PICKER_SCOPES = [
  'https://www.googleapis.com/auth/drive.file',
] as const;

export const GOOGLE_WORKSPACE_EXPORT_SCOPES = [
  'https://www.googleapis.com/auth/drive.file',
] as const;

const GOOGLE_WORKSPACE_FUNCTION = 'google-workspace';
const GOOGLE_WORKSPACE_POPUP_NAME = 'google-workspace-oauth';
export const GOOGLE_WORKSPACE_MESSAGE_TYPE = 'google-workspace-oauth-result';
const GOOGLE_WORKSPACE_PENDING_KEY = 'google-workspace-pending';

export interface GoogleWorkspaceConnectionStatus {
  connected: boolean;
  hasRequiredScopes: boolean;
  requiredScopes: string[];
  grantedScopes: string[];
  missingScopes: string[];
  googleEmail: string | null;
  googleDisplayName: string | null;
  lastError: string | null;
  revokedAt: string | null;
  accountMismatch: boolean;
  authEmail: string | null;
}

export interface GoogleWorkspacePickerTokenResult {
  accessToken: string;
  expiresIn: number;
  grantedScopes: string[];
  googleEmail: string | null;
}

export interface GoogleWorkspaceOAuthCompletionResult {
  success: boolean;
  code: string;
  message: string;
  returnTo: string;
  accountMismatch?: boolean;
  googleEmail?: string | null;
  authEmail?: string | null;
}

type GoogleWorkspaceErrorPayload = {
  code?: unknown;
  error?: unknown;
  message?: unknown;
  details?: unknown;
};

export class GoogleWorkspaceError extends Error {
  code: string;
  details?: unknown;

  constructor(message: string, code = 'GOOGLE_WORKSPACE_ERROR', details?: unknown) {
    super(message);
    this.name = 'GoogleWorkspaceError';
    this.code = code;
    this.details = details;
  }
}

export function formatGoogleWorkspaceError(
  error: unknown,
  fallback = 'Google Workspace request failed.'
) {
  const normalizedError = coerceGoogleWorkspaceError(error);
  if (normalizedError?.message.trim()) return normalizedError.message;
  if (typeof error === 'string' && error.trim()) return error.trim();
  return fallback;
}

export function hasGoogleDrivePickerConfig() {
  return Boolean(
    import.meta.env.VITE_GOOGLE_PICKER_API_KEY?.trim() &&
    import.meta.env.VITE_GOOGLE_CLOUD_APP_ID?.trim()
  );
}

export function getGoogleDrivePickerConfig() {
  return {
    developerKey: import.meta.env.VITE_GOOGLE_PICKER_API_KEY?.trim() ?? '',
    appId: import.meta.env.VITE_GOOGLE_CLOUD_APP_ID?.trim() ?? '',
  };
}

export async function getGoogleWorkspaceConnectionStatus(requiredScopes: readonly string[] = []) {
  return postGoogleWorkspaceAction<GoogleWorkspaceConnectionStatus>({
    action: 'connection_status',
    requiredScopes: [...requiredScopes],
  });
}

export async function getGoogleWorkspacePickerAccessToken(
  requiredScopes: readonly string[] = GOOGLE_WORKSPACE_PICKER_SCOPES
) {
  return postGoogleWorkspaceAction<GoogleWorkspacePickerTokenResult>({
    action: 'get_picker_access_token',
    requiredScopes: [...requiredScopes],
  });
}

export async function importGoogleDriveFiles(
  args: ResourceParentLocator & {
    fileIds: string[];
  }
) {
  return postGoogleWorkspaceAction<GoogleDriveImportResult>({
    action: 'import_drive_files',
    parentCourseId: args.parentCourseId ?? null,
    parentUnitId: args.parentUnitId ?? null,
    parentLessonId: args.parentLessonId ?? null,
    courseId: args.courseId,
    fileIds: args.fileIds,
  });
}

export async function importNativeSlides(
  args: ResourceParentLocator & {
    fileIds: string[];
  }
) {
  if (!isFeatureEnabled('googleSlidesImport')) {
    throw new GoogleWorkspaceError(
      'Google Slides import is not enabled for this environment.',
      'FEATURE_DISABLED'
    );
  }

  return postGoogleWorkspaceAction<GoogleNativeImportResult>({
    action: 'import_native_slides',
    parentCourseId: args.parentCourseId ?? null,
    parentUnitId: args.parentUnitId ?? null,
    parentLessonId: args.parentLessonId ?? null,
    courseId: args.courseId,
    fileIds: args.fileIds,
  });
}

export async function importNativeDocs(
  args: ResourceParentLocator & {
    fileIds: string[];
  }
) {
  if (!isFeatureEnabled('googleDocsImport')) {
    throw new GoogleWorkspaceError(
      'Google Docs import is not enabled for this environment.',
      'FEATURE_DISABLED'
    );
  }

  return postGoogleWorkspaceAction<GoogleNativeImportResult>({
    action: 'import_native_docs',
    parentCourseId: args.parentCourseId ?? null,
    parentUnitId: args.parentUnitId ?? null,
    parentLessonId: args.parentLessonId ?? null,
    courseId: args.courseId,
    fileIds: args.fileIds,
  });
}

// Silently re-check & re-extract a course's extractable link resources (Docs,
// Slides). Called fire-and-forget when the AI chat panel opens; the backend
// skips unchanged files.
export async function refreshCourseLinkContent(courseId: string) {
  return postGoogleWorkspaceAction<RefreshCourseLinkContentResult>({
    action: 'refresh_course_link_content',
    courseId,
  });
}

export async function exportDocumentToGoogleDocs(resourceId: string) {
  return postGoogleWorkspaceAction<GoogleDocsExportResult>({
    action: 'export_document_to_google_docs',
    resourceId,
  });
}

export async function exportSlidesToGoogleSlides(resourceId: string) {
  return postGoogleWorkspaceAction<{ presentationId: string; url: string }>({
    action: 'export_slides_to_google_slides',
    resourceId,
  });
}

export async function ensureGoogleWorkspaceScopes(requiredScopes: readonly string[]) {
  const normalizedScopes = [
    ...new Set(requiredScopes.map((scope) => scope.trim()).filter(Boolean)),
  ].sort();
  const status = await getGoogleWorkspaceConnectionStatus(normalizedScopes);
  if (status.connected && status.hasRequiredScopes) {
    return status;
  }

  const popup = openOAuthPopup();
  storePendingGoogleWorkspaceAction({
    requiredScopes: normalizedScopes,
    returnTo: getCurrentReturnTo(),
    startedAt: Date.now(),
  });

  try {
    const result = await postGoogleWorkspaceAction<{ authorizationUrl: string }>({
      action: status.connected ? 'upgrade_scopes' : 'begin_oauth',
      requiredScopes: normalizedScopes,
      returnTo: getCurrentReturnTo(),
    });

    popup.location.replace(result.authorizationUrl);
    await waitForGoogleWorkspacePopup(popup);
    const refreshed = await getGoogleWorkspaceConnectionStatus(normalizedScopes);
    if (!refreshed.connected || !refreshed.hasRequiredScopes) {
      throw new GoogleWorkspaceError(
        'Google Workspace is connected, but the required permissions were not granted.',
        'MISSING_GOOGLE_SCOPES',
        refreshed
      );
    }
    return refreshed;
  } finally {
    clearPendingGoogleWorkspaceAction();
  }
}

export async function completeGoogleWorkspaceOAuth(params: {
  state: string;
  code?: string;
  error?: string;
  errorDescription?: string;
}) {
  return postGoogleWorkspaceAction<GoogleWorkspaceOAuthCompletionResult>({
    action: 'complete_oauth',
    state: params.state,
    code: params.code,
    error: params.error,
    errorDescription: params.errorDescription,
  });
}

export async function withGoogleWorkspaceScopes<T>(
  requiredScopes: readonly string[],
  action: () => Promise<T>
) {
  await ensureGoogleWorkspaceScopes(requiredScopes);
  try {
    return await action();
  } catch (error) {
    if (!isRecoverableGoogleWorkspaceError(error)) {
      throw error;
    }
    await ensureGoogleWorkspaceScopes(requiredScopes);
    return action();
  }
}

/**
 * Force a fresh Google Workspace OAuth flow, even if already connected.
 * Useful when the user wants to switch to a different Google account.
 */
export async function reconnectGoogleWorkspace(requiredScopes: readonly string[]) {
  const normalizedScopes = [
    ...new Set(requiredScopes.map((scope) => scope.trim()).filter(Boolean)),
  ].sort();
  const popup = openOAuthPopup();
  storePendingGoogleWorkspaceAction({
    requiredScopes: normalizedScopes,
    returnTo: getCurrentReturnTo(),
    startedAt: Date.now(),
  });

  try {
    const result = await postGoogleWorkspaceAction<{ authorizationUrl: string }>({
      action: 'begin_oauth',
      requiredScopes: normalizedScopes,
      returnTo: getCurrentReturnTo(),
      forceAccountSwitch: true,
    });

    popup.location.replace(result.authorizationUrl);
    await waitForGoogleWorkspacePopup(popup);
    return await getGoogleWorkspaceConnectionStatus(normalizedScopes);
  } finally {
    clearPendingGoogleWorkspaceAction();
  }
}

function openOAuthPopup() {
  const popup = window.open('', GOOGLE_WORKSPACE_POPUP_NAME, buildPopupFeatures());
  if (!popup) {
    throw new GoogleWorkspaceError(
      'Google sign-in popup was blocked. Allow popups for this site and try again.',
      'GOOGLE_POPUP_BLOCKED'
    );
  }

  try {
    popup.document.title = 'Connecting Google Workspace';
    popup.document.body.innerHTML =
      '<p style="font-family: sans-serif; padding: 16px;">Connecting Google Workspace…</p>';
  } catch {
    // no-op
  }

  return popup;
}

async function postGoogleWorkspaceAction<T>(body: Record<string, unknown>): Promise<T> {
  const session = (await supabase.auth.getSession()).data.session;
  if (!session?.access_token) {
    throw new GoogleWorkspaceError(
      'You must be logged in to use Google Workspace features.',
      'UNAUTHORIZED'
    );
  }

  const baseUrl = import.meta.env.VITE_SUPABASE_URL?.replace(/\/$/, '');
  if (!baseUrl) {
    throw new GoogleWorkspaceError('VITE_SUPABASE_URL is not configured.', 'MISSING_SUPABASE_URL');
  }

  const response = await fetch(`${baseUrl}/functions/v1/${GOOGLE_WORKSPACE_FUNCTION}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${session.access_token}`,
      apikey: import.meta.env.VITE_SUPABASE_ANON_KEY,
    },
    body: JSON.stringify(body),
  });

  const text = await response.text();
  const payload = parseErrorPayload(text);

  if (!response.ok) {
    throw new GoogleWorkspaceError(
      typeof payload.error === 'string' && payload.error.trim()
        ? payload.error
        : `Google Workspace request failed (${response.status})`,
      typeof payload.code === 'string' ? payload.code : 'GOOGLE_WORKSPACE_ERROR',
      payload.details
    );
  }

  return parseSuccessPayload<T>(text);
}

function parseSuccessPayload<T>(text: string) {
  try {
    return JSON.parse(text) as T;
  } catch {
    throw new GoogleWorkspaceError(
      'Google Workspace returned an invalid response.',
      'INVALID_RESPONSE'
    );
  }
}

function parseErrorPayload(text: string): GoogleWorkspaceErrorPayload {
  try {
    return JSON.parse(text) as GoogleWorkspaceErrorPayload;
  } catch {
    return {};
  }
}

function buildPopupFeatures() {
  const width = 560;
  const height = 720;
  const left = Math.max(0, Math.round(window.screenX + (window.outerWidth - width) / 2));
  const top = Math.max(0, Math.round(window.screenY + (window.outerHeight - height) / 2));
  return [
    'popup=yes',
    `width=${width}`,
    `height=${height}`,
    `left=${left}`,
    `top=${top}`,
    'resizable=yes',
    'scrollbars=yes',
  ].join(',');
}

function waitForGoogleWorkspacePopup(popup: Window) {
  return new Promise<void>((resolve, reject) => {
    let settled = false;

    const cleanup = () => {
      settled = true;
      window.removeEventListener('message', onMessage);
      window.clearInterval(interval);
    };

    const onMessage = (event: MessageEvent) => {
      if (event.origin !== window.location.origin) {
        return;
      }

      const payload = event.data;
      if (
        !payload ||
        typeof payload !== 'object' ||
        payload.type !== GOOGLE_WORKSPACE_MESSAGE_TYPE
      ) {
        return;
      }

      cleanup();

      if (payload.success === true) {
        resolve();
        return;
      }

      reject(
        new GoogleWorkspaceError(
          typeof payload.message === 'string' && payload.message.trim()
            ? payload.message
            : 'Google authorization was not completed.',
          typeof payload.code === 'string' ? payload.code : 'GOOGLE_AUTH_FAILED'
        )
      );
    };

    const interval = window.setInterval(() => {
      if (settled) return;
      if (!popup.closed) return;
      cleanup();
      reject(
        new GoogleWorkspaceError(
          'Google sign-in window was closed before authorization completed.',
          'GOOGLE_POPUP_CLOSED'
        )
      );
    }, 400);

    window.addEventListener('message', onMessage);
  });
}

function isRecoverableGoogleWorkspaceError(error: unknown) {
  const normalizedError = coerceGoogleWorkspaceError(error);
  return Boolean(
    normalizedError &&
    ['GOOGLE_CONNECTION_REQUIRED', 'MISSING_GOOGLE_SCOPES', 'GOOGLE_RECONNECT_REQUIRED'].includes(
      normalizedError.code
    )
  );
}

function coerceGoogleWorkspaceError(error: unknown) {
  if (error instanceof GoogleWorkspaceError) return error;

  const maybeError =
    error && typeof error === 'object'
      ? (error as {
          code?: unknown;
          details?: unknown;
          error?: unknown;
          message?: unknown;
          data?: GoogleWorkspaceErrorPayload;
        })
      : null;

  const payload = maybeError?.data && typeof maybeError.data === 'object' ? maybeError.data : null;

  const message = [
    typeof payload?.error === 'string' ? payload.error : null,
    typeof payload?.message === 'string' ? payload.message : null,
    typeof maybeError?.error === 'string' ? maybeError.error : null,
    typeof maybeError?.message === 'string' ? maybeError.message : null,
    error instanceof Error ? error.message : null,
  ].find((value): value is string => Boolean(value && value.trim()));

  const code = [
    typeof payload?.code === 'string' ? payload.code : null,
    typeof maybeError?.code === 'string' ? maybeError.code : null,
  ].find((value): value is string => Boolean(value && value.trim()));

  const details = payload?.details ?? maybeError?.details;
  if (!message && !code && typeof details === 'undefined') {
    return null;
  }

  return new GoogleWorkspaceError(
    message?.trim() || 'Google Workspace request failed.',
    code?.trim() || 'GOOGLE_WORKSPACE_ERROR',
    details
  );
}

function storePendingGoogleWorkspaceAction(payload: Record<string, unknown>) {
  sessionStorage.setItem(GOOGLE_WORKSPACE_PENDING_KEY, JSON.stringify(payload));
}

function clearPendingGoogleWorkspaceAction() {
  sessionStorage.removeItem(GOOGLE_WORKSPACE_PENDING_KEY);
}

function getCurrentReturnTo() {
  return `${window.location.pathname}${window.location.search}${window.location.hash}`;
}
